import { motion } from 'framer-motion';
import { FileText } from 'lucide-react';

const TermsPage = () => {
  const lastUpdated = 'January 1, 2025';

  return (
    <main className="min-h-screen pt-32 pb-24 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-20 h-20 mx-auto rounded-3xl bg-gradient-to-br from-cyclara-400 to-lavender-500 
                        flex items-center justify-center mb-6 shadow-lg shadow-cyclara-500/30">
            <FileText className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-midnight-500 mb-4">
            Terms of Service
          </h1>
          <p className="text-midnight-400">
            Last updated: {lastUpdated}
          </p>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl shadow-xl shadow-midnight-500/5 border border-cyclara-100 p-8 md:p-12 space-y-8"
        >
          <section>
            <h2 className="text-2xl font-bold text-midnight-500 mb-4">1. Acceptance of Terms</h2>
            <p className="text-midnight-400">
              By downloading, installing, or using Cyclara ("the App"), you agree to be bound by these 
              Terms of Service. If you do not agree to these terms, please do not use the App.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-500 mb-4">2. Description of Service</h2>
            <p className="text-midnight-400">
              Cyclara is a privacy-first mobile application designed to help users track their menstrual 
              cycle, log moods and symptoms, and receive AI-powered wellness support. The App operates 
              entirely offline and stores all data locally on your device.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-500 mb-4">3. Not Medical Advice</h2>
            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
              <p className="text-amber-800 font-semibold mb-2">Important Disclaimer</p>
              <p className="text-amber-700">
                Cyclara is not a medical device and does not provide medical advice, diagnosis, or treatment. 
                The AI companion and cycle predictions are for informational and wellness purposes only. 
                Always consult a qualified healthcare provider for medical concerns.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-500 mb-4">4. User Responsibilities</h2>
            <p className="text-midnight-400 mb-4">By using Cyclara, you agree to:</p>
            <ul className="space-y-2 text-midnight-400">
              <li className="flex items-start gap-2">
                <span className="text-cyclara-500">•</span>
                Use the App for personal, non-commercial purposes only
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyclara-500">•</span>
                Not attempt to reverse engineer or modify the App
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyclara-500">•</span>
                Take responsibility for backing up your local data
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyclara-500">•</span>
                Not use the App for any illegal purposes
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-500 mb-4">5. Intellectual Property</h2>
            <p className="text-midnight-400">
              All content, features, and functionality of Cyclara, including but not limited to text, 
              graphics, logos, and software, are owned by Cyclara and are protected by copyright, 
              trademark, and other intellectual property laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-500 mb-4">6. Data and Privacy</h2>
            <p className="text-midnight-400">
              Your use of Cyclara is also governed by our Privacy Policy. By using the App, you consent 
              to the practices described in the Privacy Policy. Since all data is stored locally, you are 
              responsible for your device's security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-500 mb-4">7. Limitation of Liability</h2>
            <p className="text-midnight-400">
              Cyclara is provided "as is" without warranties of any kind. We shall not be liable for any 
              damages arising from your use of the App, including but not limited to data loss, health 
              decisions made based on App information, or device issues.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-500 mb-4">8. Changes to Terms</h2>
            <p className="text-midnight-400">
              We may update these Terms of Service from time to time. We will notify users of any 
              material changes through the App or our website. Continued use of the App after changes 
              constitutes acceptance of the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-midnight-500 mb-4">9. Contact Us</h2>
            <p className="text-midnight-400">
              If you have any questions about these Terms of Service, please contact us at{' '}
              <a href="mailto:legal@cyclara.app" className="text-cyclara-500 hover:underline">
                legal@cyclara.app
              </a>
            </p>
          </section>
        </motion.div>
      </div>
    </main>
  );
};

export default TermsPage;
